/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import {
  TextStyle,
  StyleProp,
  View,
  ViewStyle,
  ScrollView,
} from "react-native";
import { useLazyQuery } from "@apollo/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import { GetUserSessionListQuery } from "graphql/internal/generated";
import { GET_USER_SESSION_LIST } from "graphql/internal/Queries";
import { getNodes } from "config/Utilities";
import { NodeType } from "config/UtilityTypes";
import { LoadingDots, Text } from "UpduoLibrary";
import { dashboardStyles } from "./shared_components";
import ActivityLogRow from "./ActivityLogRow";
import { upduoMediumGray } from "config/UpduoColors";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function ActivityLogWidget({
  style,
}: {
  style: StyleProp<ViewStyle>;
}) {
  const [sessionList, setSessionList] = useState<
    NodeType<GetUserSessionListQuery, "userSessions">[]
  >([]);
  const [totalSessions, setTotalSessions] = useState<number>(0);

  const [getUserSessionList, { loading }] =
    useLazyQuery<GetUserSessionListQuery>(GET_USER_SESSION_LIST, {
      fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
      onCompleted: (result) => {
        if (result?.userSessions) {
          setSessionList(getNodes(result.userSessions));
          setTotalSessions(result.userSessions?.completedCount || 0);
        }
      },
      onError: (error) => {
        console.error("ERROR getUserSessionList", error);
        UpduoAnalytics.logEvent("app-dashboard-getusersessionlist-error", {
          error,
        });
      },
    });

  useEffect(() => {
    getUserSessionList({
      variables: {
        first: 10,
        getUserSessionListRequest: {},
      },
    });
  }, []);

  const headerStyle: TextStyle = {
    textTransform: "uppercase",
    color: upduoMediumGray,
  };

  return (
    <View style={[{ padding: 20 }, dashboardStyles.dashboardWidget, style]}>
      <Text
        style={[
          dashboardStyles.widgetTitle,
          {
            marginBottom: 16,
          },
        ]}
      >
        {"Activity Log"}
      </Text>
      {loading ? (
        <View
          style={{
            width: "100%",
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingDots />
        </View>
      ) : totalSessions ? (
        <ScrollView>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Text style={headerStyle}>{"Date/Time"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={headerStyle}>{"Conversation"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={headerStyle}>{"Matched with"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={headerStyle}>{"Length"}</Text>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessionList.map((session, i) => (
                <ActivityLogRow key={session.id} session={session} index={i} />
              ))}
            </TableBody>
          </Table>
        </ScrollView>
      ) : (
        <View
          style={{
            width: "100%",
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ color: upduoMediumGray, textAlign: "center" }}>
            {"No sessons completed yet"}
          </Text>
        </View>
      )}
    </View>
  );
}
